import { useEffect, useState, type CSSProperties } from "react";
import { apiClient } from "@/lib/api-client.js";
import { eur } from "@/lib/format.js";

interface FoerderStaffel {
  bisKwp: number;
  satzEur: number;
}

interface FoerderungItem {
  id: string;
  name: string;
  foerderstelle: string;
  ebene: "bund" | "bundesland";
  bundesland: string;
  foerdertyp: "einmalig" | "speicher" | "pauschale" | "info";
  betragProKwp?: number;
  staffel?: FoerderStaffel[];
  betragProKwh?: number;
  deckelKwh?: number;
  pauschaleEur?: number;
  maxBetrag?: number;
  minKwp?: number;
  minKwh?: number;
  maxKwp?: number;
  betragText?: string;
  voraussetzungen: string[];
  zielgruppe?: string;
  url: string;
  gueltigBis?: string;
  status: "belegt" | "pruefen";
  stand: string;
}

interface FoerderungsBerechnung {
  foerderung: FoerderungItem;
  anlageKwp: number;
  speicherKwh: number;
  berechneterBetrag: number;
  hinweis?: string;
}

interface FoerderungenResponse {
  stand: string;
  items: FoerderungItem[];
}

interface BerechnungResponse {
  stand: string;
  berechnungen: FoerderungsBerechnung[];
}

// Alle 9 Bundesländer (+ "Alle"). "Bund"-Förderungen gelten immer mit.
const BUNDESLAND_TABS: { key: string; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "burgenland", label: "Burgenland" },
  { key: "kaernten", label: "Kärnten" },
  { key: "niederoesterreich", label: "Niederösterreich" },
  { key: "oberoesterreich", label: "Oberösterreich" },
  { key: "salzburg", label: "Salzburg" },
  { key: "steiermark", label: "Steiermark" },
  { key: "tirol", label: "Tirol" },
  { key: "vorarlberg", label: "Vorarlberg" },
  { key: "wien", label: "Wien" },
];

const BL_LABEL: Record<string, string> = {
  all: "Bund",
  burgenland: "Burgenland",
  kaernten: "Kärnten",
  niederoesterreich: "Niederösterreich",
  oberoesterreich: "Oberösterreich",
  salzburg: "Salzburg",
  steiermark: "Steiermark",
  tirol: "Tirol",
  vorarlberg: "Vorarlberg",
  wien: "Wien",
};

export function FoerderungenPage() {
  const [foerderungen, setFoerderungen] = useState<FoerderungItem[]>([]);
  const [berechnungen, setBerechnungen] = useState<FoerderungsBerechnung[]>([]);
  const [stand, setStand] = useState<string>("");
  const [bundesland, setBundesland] = useState("tirol");
  const [kwp, setKwp] = useState<string>("10");
  const [speicherKwh, setSpeicherKwh] = useState<string>("0");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void loadFoerderungen();
  }, [bundesland]);

  useEffect(() => {
    const kwpNum = parseFloat(kwp);
    if (kwpNum > 0) void loadBerechnung(kwpNum, parseFloat(speicherKwh) || 0);
    else setBerechnungen([]);
  }, [kwp, speicherKwh, bundesland]);

  const loadFoerderungen = async () => {
    setLoading(true);
    const res = await apiClient.get<FoerderungenResponse>(`/foerderungen?bundesland=${bundesland}`);
    if (res.success) {
      setFoerderungen(res.data.items);
      setStand(res.data.stand);
    }
    setLoading(false);
  };

  const loadBerechnung = async (kwpNum: number, kwhNum: number) => {
    const res = await apiClient.get<BerechnungResponse>(
      `/foerderungen/berechnung?bundesland=${bundesland}&kwp=${kwpNum}&speicherKwh=${kwhNum}`,
    );
    if (res.success) setBerechnungen(res.data.berechnungen);
  };

  const istKonkret = bundesland !== "all";
  const gesamtSumme = berechnungen.reduce((s, b) => s + b.berechneterBetrag, 0);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">💰 Förderungen</h1>
        <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          Stand: {stand || "…"} · alle Bundesländer · unverbindlich, bei der Förderstelle prüfen
        </span>
      </div>

      {/* Rechner */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>🧮 Förderungsrechner</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: 12, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
              PV-Anlage
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number"
                value={kwp}
                min={1}
                max={1000}
                step={0.5}
                onChange={(e) => setKwp(e.target.value)}
                style={inputStyle}
              />
              <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>kWp</span>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
              Speicher (optional)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                type="number"
                value={speicherKwh}
                min={0}
                max={100}
                step={0.5}
                onChange={(e) => setSpeicherKwh(e.target.value)}
                style={inputStyle}
              />
              <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>kWh</span>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
              Bundesland
            </label>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 560 }}>
              {BUNDESLAND_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setBundesland(t.key)}
                  style={{
                    padding: "8px 12px",
                    fontSize: 13,
                    borderRadius: 6,
                    border: "1px solid var(--color-border)",
                    cursor: "pointer",
                    background: bundesland === t.key ? "var(--color-primary)" : "#fff",
                    color: bundesland === t.key ? "#fff" : "inherit",
                    fontWeight: bundesland === t.key ? 600 : 400,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {istKonkret && gesamtSumme > 0 && (
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Mögliche Förderung gesamt</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>{eur(gesamtSumme)}</div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                Bund + {BL_LABEL[bundesland]} · unverbindliche Schätzung
              </div>
            </div>
          )}
        </div>

        {!istKonkret && (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--color-text-muted)" }}>
            ℹ️ Wähle ein Bundesland für die konkrete Fördersumme — Landesförderungen sind nicht länderübergreifend
            kombinierbar.
          </div>
        )}

        {/* Berechnungs-Aufschlüsselung */}
        {istKonkret && berechnungen.length > 0 && (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {berechnungen.map((b) => (
              <div
                key={b.foerderung.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 14px",
                  background: "#f8fafc",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                }}
              >
                <div>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{b.foerderung.name}</span>
                  {b.foerderung.status === "pruefen" && (
                    <span style={{ fontSize: 11, color: "#b45309", marginLeft: 8 }}>⚠ zu prüfen</span>
                  )}
                  {b.hinweis && (
                    <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>({b.hinweis})</span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: b.foerderung.ebene === "bund" ? "#2563eb" : "#16a34a",
                  }}
                >
                  {eur(b.berechneterBetrag)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Förderungs-Karten */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>Lade…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {foerderungen.map((f) => (
            <div key={f.id} className="card" style={{ padding: 16, display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{f.name}</h3>
                  <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>
                    {f.foerderstelle}
                    {f.zielgruppe && ` · ${f.zielgruppe}`}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 10,
                    padding: "3px 7px",
                    borderRadius: 10,
                    background: f.ebene === "bund" ? "#dbeafe" : "#dcfce7",
                    color: f.ebene === "bund" ? "#1e40af" : "#166534",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {f.ebene === "bund" ? "Bund" : BL_LABEL[f.bundesland]}
                </span>
              </div>

              {/* Betrag je nach Fördertyp */}
              {f.foerdertyp === "einmalig" && f.betragProKwp !== undefined && (
                <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a", marginBottom: 8 }}>
                  {f.betragProKwp} €/kWp
                  {f.staffel && (
                    <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}> (gestaffelt)</span>
                  )}
                  {f.maxBetrag && (
                    <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                      {" "}
                      (max. {eur(f.maxBetrag)})
                    </span>
                  )}
                </div>
              )}

              {f.foerdertyp === "speicher" && f.betragProKwh !== undefined && (
                <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb", marginBottom: 8 }}>
                  {f.betragProKwh} €/kWh
                  {f.deckelKwh && (
                    <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                      {" "}
                      (max. {f.deckelKwh} kWh)
                    </span>
                  )}
                  {f.maxBetrag && (
                    <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                      {" "}
                      (max. {eur(f.maxBetrag)})
                    </span>
                  )}
                </div>
              )}

              {f.foerdertyp === "pauschale" && f.pauschaleEur !== undefined && (
                <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a", marginBottom: 8 }}>
                  {eur(f.pauschaleEur)} <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>pauschal</span>
                </div>
              )}

              {f.foerdertyp === "info" && f.betragText && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: "#374151",
                    marginBottom: 8,
                    fontStyle: "italic",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "8px 10px",
                  }}
                >
                  {f.betragText}
                </div>
              )}

              <ul style={{ fontSize: 12, color: "#374151", paddingLeft: 16, margin: "0 0 10px" }}>
                {f.voraussetzungen.map((v, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    {v}
                  </li>
                ))}
              </ul>

              {/* Footer: Status + Stand + Quelle */}
              <div
                style={{
                  marginTop: "auto",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    padding: "2px 7px",
                    borderRadius: 10,
                    background: f.status === "belegt" ? "#dcfce7" : "#fef3c7",
                    color: f.status === "belegt" ? "#166534" : "#92400e",
                    whiteSpace: "nowrap",
                  }}
                  title={
                    f.status === "belegt"
                      ? "Aus offizieller Quelle bestätigt"
                      : "Zeitkritisch / einzelfallabhängig — vor Antrag bestätigen"
                  }
                >
                  {f.status === "belegt" ? "✓ belegt" : "⚠ zu prüfen"}
                </span>
                <span style={{ fontSize: 11, color: "#6b7280" }}>
                  Stand {f.stand}
                  {f.gueltigBis && f.gueltigBis !== "laufend" ? ` · ${f.gueltigBis}` : f.gueltigBis === "laufend" ? " · laufend" : ""}
                </span>
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: "var(--color-primary)" }}
                >
                  Quelle →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const inputStyle: CSSProperties = {
  width: 90,
  padding: "8px 10px",
  border: "1px solid var(--color-border)",
  borderRadius: 6,
  fontSize: 14,
};
